import { Grid, MenuItem, Typography } from "@mui/material"
import { CreateUpdateLocation, HistoryType } from "../locationsTypes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import {
    FormikDropdown,
    FormikMultipleAutoComplete,
    FormikSwitch,
    FormikTextField,
} from "../../../../../../shared/components/forms/Formik"
import { useDateTime } from "../../../../../localization/useDateTime"
import { CompanyAdminLocationsQuery, ShopsForCompanyResponse, fetchShopsForCompany } from "../locationsApi"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useCurrentCompany } from "../../../../hooks/useCurrentCompany"
import { StandardForm } from "../../../../../../shared/components/forms/StandardForm"
import { StandardMainPanel } from "../../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../../shared/components/forms/StandardSidePanel"

const selectShops = (data: ShopsForCompanyResponse) => data?.shops || []

type CreateUpdateLocationFormContentProps = Readonly<{
    history?: HistoryType
}>
export function CreateUpdateLocationFormContent({ history }: CreateUpdateLocationFormContentProps) {
    const { companyId } = useCurrentCompany()
    const dateTimeFactory = useDateTime()

    const ShopsResult = useApiQuery(fetchShopsForCompany, {
        queryName: CompanyAdminLocationsQuery.SHOPS,
        dependencies: {
            companyId,
        },
        select: selectShops,
    })

    return (
        <>
            <StandardForm>
                <StandardMainPanel>
                    <LocalizedStrict
                        id="create-or-edit-company-location-field-name"
                        attrs={{ label: true, placeholder: true }}
                    >
                        <FormikTextField name="name" label="Name" placeholder="Enter name" fullWidth />
                    </LocalizedStrict>

                    <Grid container flexWrap="nowrap" gap={2.5}>
                        <Grid item xs={8}>
                            <LocalizedStrict
                                id="create-or-edit-company-location-field-street"
                                attrs={{ label: true, placeholder: true }}
                            >
                                <FormikTextField
                                    name="address.street"
                                    label="Street"
                                    placeholder="Enter street"
                                    fullWidth
                                />
                            </LocalizedStrict>
                        </Grid>
                        <Grid item xs={4}>
                            <LocalizedStrict
                                id="create-or-edit-company-location-field-streetNumber"
                                attrs={{ label: true, placeholder: true }}
                            >
                                <FormikTextField
                                    name="address.streetNumber"
                                    label="Street no"
                                    placeholder="Enter street no"
                                    fullWidth
                                />
                            </LocalizedStrict>
                        </Grid>
                    </Grid>
                    <Grid container flexWrap="nowrap" gap={2.5}>
                        <Grid item xs={4}>
                            <LocalizedStrict
                                id="create-or-edit-company-location-field-postalCode"
                                attrs={{ label: true, placeholder: true }}
                            >
                                <FormikTextField
                                    name="address.postalCode"
                                    label="Postal code"
                                    placeholder="Enter postal code"
                                    fullWidth
                                />
                            </LocalizedStrict>
                        </Grid>
                        <Grid item xs={8}>
                            <LocalizedStrict
                                id="create-or-edit-company-location-field-city"
                                attrs={{ label: true, placeholder: true }}
                            >
                                <FormikTextField name="address.city" label="City" placeholder="Enter city" fullWidth />
                            </LocalizedStrict>
                        </Grid>
                    </Grid>
                    <LocalizedStrict id="create-or-edit-company-location-field-country" attrs={{ label: true }}>
                        <FormikDropdown defaultValue="DK" name="address.country.code" label="Country" fullWidth>
                            <MenuItem value="DK">
                                <LocalizedStrict id="create-or-edit-company-location-field-country-denmark-item">
                                    <>Denmark</>
                                </LocalizedStrict>
                            </MenuItem>
                        </FormikDropdown>
                    </LocalizedStrict>
                    <LocalizedStrict id="create-or-edit-company-location-field-shops" attrs={{ label: true }}>
                        <FormikMultipleAutoComplete
                            name="shops"
                            label="Connected shops"
                            fullWidth
                            options={ShopsResult.data || []}
                            isOptionEqualToValue={(option, value) => option.id === value}
                            areOptionsEqual={(x, y) => x.id === y.id}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                        />
                    </LocalizedStrict>
                </StandardMainPanel>
                <LocalizedStrict id="create-or-edit-company-location-settings" attrs={{ title: true }}>
                    <StandardSidePanel title="Settings" variant="collapsible">
                        <LocalizedStrict id="create-or-edit-company-location-field-active">
                            <FormikSwitch<CreateUpdateLocation> name="isActive">Active</FormikSwitch>
                        </LocalizedStrict>
                    </StandardSidePanel>
                </LocalizedStrict>
                {history && (
                    <LocalizedStrict id="create-or-edit-company-location-history" attrs={{ title: true }}>
                        <StandardSidePanel title="History">
                            <Grid container item>
                                <Grid item xs={12} sm={5} justifyContent="space-between">
                                    <LocalizedStrict id="history-created">
                                        <Typography variant="body2">Created:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs={12} sm={7}>
                                    <Typography variant="body2" textAlign="right">
                                        {dateTimeFactory.formatDateTime(
                                            dateTimeFactory.fromApi(history.created.timestamp),
                                        )}
                                    </Typography>
                                    <Typography variant="body2" textAlign="right">
                                        {history.created.author ?? "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container item>
                                <Grid item xs={12} sm={5}>
                                    <LocalizedStrict id="history-edited">
                                        <Typography variant="body2">Edited:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs={12} sm={7}>
                                    <Typography variant="body2" textAlign="right">
                                        {history.updated
                                            ? dateTimeFactory.formatDateTime(
                                                  dateTimeFactory.fromApi(history.updated.timestamp),
                                              )
                                            : "-"}
                                    </Typography>
                                    <Typography variant="body2" textAlign="right">
                                        {history.updated?.author ?? "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </StandardSidePanel>
                    </LocalizedStrict>
                )}
            </StandardForm>
        </>
    )
}
